import './accountMenu.scss';

import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Icon, { ICON_TYPE } from 'components/icon';

import useUserContext from '../../context/user';

const MENU_ITEM_SELECTOR = '[role="menuitem"]';

// Header dropdown that is the single navigation for the account section:
// signed-in email, section links and Sign out. The current section is derived
// from the route so the header needs no per-page wiring. Above `labelBreakpoint`
// the trigger is the "MY ACCOUNT" text, below it the avatar icon.
const AccountMenu = (props) => {
  const userContext = useUserContext();
  const router = useRouter();
  const menuId = useId();
  const rootRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const close = ({ restoreFocus = false } = {}) => {
    setIsOpen(false);
    if (restoreFocus) {
      toggleRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    menuRef.current?.querySelector(MENU_ITEM_SELECTOR)?.focus();

    const handleMouseDown = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  const handleMenuKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close({ restoreFocus: true });
      return;
    }

    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();

    const items = Array.from(menuRef.current.querySelectorAll(MENU_ITEM_SELECTOR));
    const index = items.indexOf(document.activeElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    items[(index + step + items.length) % items.length]?.focus();
  };

  const handleSignOutClick = () => {
    close();
    props.onSignOut();
  };

  const { email } = userContext.data;
  const currentPath = router.asPath.split(/[?#]/)[0];
  const labelClassName = `d-none d-${props.labelBreakpoint}-inline-flex`;
  const avatarClassName = `d-${props.labelBreakpoint}-none`;

  return (
    <div ref={rootRef} className={classNames('account-menu', props.className)}>
      <button
        ref={toggleRef}
        type="button"
        className="account-menu-toggle"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={classNames('header-nav-link account-menu-label', labelClassName)}>
          {props.label}
          <span className="account-menu-caret" aria-hidden="true" />
        </span>
        <span className={classNames('account-menu-avatar', avatarClassName, props.avatarClassName)}>
          <Icon type={ICON_TYPE.AVATAR} />
          <span className="sr-only">{props.label}</span>
        </span>
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={props.label}
          className="dropdown-menu dropdown-menu-right show account-menu-list"
          onKeyDown={handleMenuKeyDown}
        >
          {email && (
            <div className="dropdown-header account-menu-header">
              Signed in as
              <br />
              <strong>{email}</strong>
            </div>
          )}

          {props.sections.map((section) => {
            const isCurrent = section.href === currentPath;

            return (
              <Link key={section.key} href={section.href} passHref legacyBehavior>
                <a
                  role="menuitem"
                  className={classNames('dropdown-item account-menu-item', {
                    'account-menu-item-current': isCurrent,
                  })}
                  aria-current={isCurrent ? 'page' : undefined}
                  onClick={() => close()}
                >
                  {section.label}
                </a>
              </Link>
            );
          })}

          <div className="dropdown-divider" role="separator" />

          <Link href={props.signOutHref} passHref legacyBehavior>
            <a
              role="menuitem"
              className="dropdown-item account-menu-item"
              onClick={handleSignOutClick}
            >
              Sign out
            </a>
          </Link>
        </div>
      )}
    </div>
  );
};

AccountMenu.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ).isRequired,
  signOutHref: PropTypes.string.isRequired,
  onSignOut: PropTypes.func.isRequired,
  label: PropTypes.string,
  labelBreakpoint: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  avatarClassName: PropTypes.string,
  className: PropTypes.string,
};

AccountMenu.defaultProps = {
  label: 'MY ACCOUNT',
  labelBreakpoint: 'md',
};

export default AccountMenu;
